import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { healthAssessmentsApi, HealthAssessment } from '../../api/healthAssessments';

function scoreColor(percentage?: number) {
  if (percentage == null) return 'text-slate-600 bg-slate-100';
  if (percentage >= 80) return 'text-green-700 bg-green-100';
  if (percentage >= 60) return 'text-amber-700 bg-amber-100';
  return 'text-red-700 bg-red-100';
}

export default function HealthAssessmentsList() {
  const [assessments, setAssessments] = useState<HealthAssessment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    healthAssessmentsApi
      .getAll()
      .then(setAssessments)
      .catch(() => setAssessments([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-cyan-600" />
      </div>
    );
  }

  if (assessments.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 py-10 text-center text-sm text-slate-500">
        No structured health assessments yet. Start one from Quick add or Care Plans shortcuts.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {assessments
        .sort((a, b) => new Date(b.assessmentDate).getTime() - new Date(a.assessmentDate).getTime())
        .map((a) => {
          const pct = a.overallScore?.percentage;
          const completedSteps = a.assessmentSteps?.filter((s) => s.completed).length ?? 0;
          const totalSteps = a.assessmentSteps?.length ?? 0;
          return (
            <div key={a.id} className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                  <MagnifyingGlassIcon className="mt-0.5 h-5 w-5 text-purple-600" />
                  <div>
                    <p className="font-medium text-slate-900">{a.birdName || a.birdId}</p>
                    <p className="text-sm text-slate-600">
                      {format(new Date(a.assessmentDate), 'dd MMM yyyy')} · {a.status}
                      {totalSteps > 0 ? ` · ${completedSteps}/${totalSteps} steps` : ''}
                    </p>
                    {a.recommendations?.length > 0 && (
                      <p className="mt-1 text-xs text-slate-500 line-clamp-2">{a.recommendations[0]}</p>
                    )}
                  </div>
                </div>
                {pct != null && (
                  <span className={`rounded-full px-2.5 py-1 text-sm font-semibold ${scoreColor(pct)}`}>
                    {pct}%
                  </span>
                )}
              </div>
            </div>
          );
        })}
    </div>
  );
}
